import { useRef, useState } from 'react';
import { Alert, Button, Form, Modal, ProgressBar, Table } from 'react-bootstrap';
import { parseTrackingCSV } from '@/pages/oms/orders/utils/trackingImport';
import type { TrackingImportRow } from '@/pages/oms/orders/utils/trackingImport';
import type { FulfillmentAdminClient } from '@/infrastructure/FulfillmentAdminClient';
import { ULTIMATE_MODAL } from '@/pages/fulfillment/components/ultimateModal';

interface ImportTrackingModalProps {
  readonly show: boolean;
  readonly client: FulfillmentAdminClient;
  readonly onHide: () => void;
  readonly onImported: () => void;
}

interface ImportProgress {
  total: number;
  processed: number;
  succeeded: number;
  failed: number;
  errors: string[];
}

const PREVIEW_ROWS = 10;

/** The OMS import CSV, applied to 2.0: same four columns and the same parser, so an operator's file works on either screen. */
export function ImportTrackingModal({ show, client, onHide, onImported }: ImportTrackingModalProps) {
  const [rows, setRows] = useState<TrackingImportRow[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setRows([]);
    setParseErrors([]);
    setProgress(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    reset();

    const reader = new FileReader();
    reader.onload = (loaded) => {
      const { data, errors } = parseTrackingCSV(String(loaded.target?.result ?? ''));
      setRows(data);
      setParseErrors(errors);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (rows.length === 0) {
      return;
    }
    setImporting(true);
    const running: ImportProgress = { total: rows.length, processed: 0, succeeded: 0, failed: 0, errors: [] };
    setProgress(running);

    for (const row of rows) {
      try {
        // No carrier column in the CSV, so the field is omitted and the stored carrier survives.
        await client.correctOrderTracking(Number(row.orderId), { number: row.trackingNumber });
        running.succeeded++;
      } catch (error) {
        running.failed++;
        running.errors.push(`Order ${row.orderId}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
      running.processed++;
      setProgress({ ...running });
    }

    setImporting(false);
    if (running.succeeded > 0) {
      onImported();
    }
  };

  const handleClose = () => {
    if (importing) {
      return;
    }
    reset();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onHide();
  };

  const done = progress !== null && progress.processed === progress.total && !importing;

  return (
    <Modal show={show} onHide={handleClose} size="lg" {...ULTIMATE_MODAL}>
      <Modal.Header closeButton>
        <Modal.Title>Import Tracking Numbers</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3" controlId="fulfillment-import-file">
          <Form.Label>Select CSV File</Form.Label>
          <Form.Control ref={fileInputRef} type="file" accept=".csv" onChange={handleFileChange} disabled={importing} />
          <Form.Text className="text-muted">
            Expected format: Order ID, Country Code, Tracking Number, Status. The Status column is validated but not
            applied — 2.0 derives status from the fulfillment ledger.
          </Form.Text>
        </Form.Group>

        {parseErrors.length > 0 && (
          <Alert variant="danger">
            <Alert.Heading className="h6">Validation Errors</Alert.Heading>
            <ul className="mb-0 small">
              {parseErrors.slice(0, PREVIEW_ROWS).map((message) => (
                <li key={message}>{message}</li>
              ))}
              {parseErrors.length > PREVIEW_ROWS && <li>... and {parseErrors.length - PREVIEW_ROWS} more errors</li>}
            </ul>
          </Alert>
        )}

        {rows.length > 0 && !progress && (
          <div className="mt-3">
            <h6>Preview{rows.length > PREVIEW_ROWS ? ` (first ${PREVIEW_ROWS} of ${rows.length})` : ''}:</h6>
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Country</th>
                  <th>Tracking Number</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, PREVIEW_ROWS).map((row) => (
                  <tr key={`${row.orderId}-${row.trackingNumber}`}>
                    <td>{row.orderId}</td>
                    <td>{row.countryCode}</td>
                    <td>
                      <code>{row.trackingNumber}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {progress && (
          <div className="mt-3">
            <ProgressBar
              now={Math.round((progress.processed / progress.total) * 100)}
              label={`${progress.processed} / ${progress.total}`}
              animated={importing}
              variant={progress.failed > 0 ? 'warning' : 'primary'}
            />
            <div className="d-flex gap-3 mt-2 small">
              <span className="text-success">Succeeded: {progress.succeeded}</span>
              {progress.failed > 0 && <span className="text-danger">Failed: {progress.failed}</span>}
            </div>
            {progress.errors.length > 0 && (
              <Alert variant="danger" className="mt-2">
                <ul className="mb-0 small">
                  {progress.errors.slice(0, PREVIEW_ROWS).map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                  {progress.errors.length > PREVIEW_ROWS && (
                    <li>... and {progress.errors.length - PREVIEW_ROWS} more errors</li>
                  )}
                </ul>
              </Alert>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose} disabled={importing}>
          {done ? 'Close' : 'Cancel'}
        </Button>
        {!done && (
          <Button variant="dark" onClick={handleImport} disabled={rows.length === 0 || parseErrors.length > 0 || importing}>
            {importing ? 'Importing...' : `Import ${rows.length} Orders`}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
